import * as React from 'react';
import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import { IconButton, Box, Button, Chip, Stack, Typography, LinearProgress } from '@mui/material';
import { ArrowUpward, ArrowDownward, ExpandMore, ExpandLess } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '../contexts/ThemeContext';
import { Link as RouterLink } from 'react-router-dom';
import { internalProblemPathFromName } from '../utils/problemLinks';

const lc_questions = [
    'Fizz Buzz',
    'Two Sum',
    'Valid Anagram',
    'Reverse String',
    'Valid Palindrome',
    //'Contains Duplicate',
    'Longest Common Prefix',
    //'First Unique Character in a String',
    'Power of Three',
    //'Reverse Bits',
    'Number of 1 Bits',
    'Range Sum Query - Immutable',
    'Fibonacci Number',
    'Climbing Stairs',
    'Min Cost Climbing Stairs',
    'Best Time to Buy and Sell Stock',
    'Binary Tree Inorder Traversal',
    'Invert Binary Tree',
    'Symmetric Tree',
    'Maximum Depth of Binary Tree',
    'Same Tree',
    //'Sum of Left Leaves',
    'Path Sum',
    'Minimum Depth of Binary Tree',
    'Validate Binary Search Tree',
    'Reverse Linked List',
    'Middle of the Linked List',
    'Binary Tree Level Order Traversal',
    //'Average of Levels in Binary Tree',
    'Find if Path Exists in Graph',
    //'Find Center of Star Graph',
    'Number of Islands',
    'Max Area of Island',
    'Count Sub Islands',
    //'Find All Groups of Farmland',
    'Valid Parentheses',
    //'Valid Parenthesis String',
    //'Range Sum Query 2D - Immutable', 
    'Product of Array Except Self',
    'Binary Search',
    //'Minimum Number of Swaps to Make the String Balanced',
    'Minimum Add to Make Parentheses Valid',
    'Longest Palindromic Substring',
   // 'Reverse Integer',
    'Two Sum II - Input Array Is Sorted',
    'Container With Most Water',
    '3Sum',
    'Group Anagrams',
    'Kth Largest Element in an Array',
    'K Closest Points to Origin',
    'Insert Delete GetRandom O(1)',
    'First Bad Version',
    'Find Peak Element',
    'Sqrt(x)',
    'Koko Eating Bananas',
    'Subarray Sum Equals K',
    'Continuous Subarray Sum',
    //'Subarray Sums Divisible by K',
    'Count Number of Nice Subarrays',
    //'Number of Pairs of Strings With Concatenation Equal to Target',
    'Clone Graph',
    'Copy List with Random Pointer',
    'Is Graph Bipartite',
    'Evaluate Division',
    // dp 2
    'Maximum Subarray',
    'Maximum Product Subarray',
    //'Maximum Sum Circular Subarray',
    //'Maximum Absolute Sum of Any Subarray',
    'Unique Paths',
    'Minimum Path Sum',
    //'Minimum Path Cost in a Grid',
    'Longest Substring Without Repeating Characters',
    'Max Consecutive Ones III',
    'Maximize the Confusion of An Exam',
    //'Frequency of the Most Frequent Element',
    //'Count Subarrays With Score Less Than K',
    'Longest Nice Subarray',
    //'Subarrays with K Different Integers',
    'Random Pick with Weight',
    'Word Break',
    'House Robber',
    //'House Robber II',
    //'Decode Ways',
    'Coin Change',
    'Longest Increasing Subsequence',
    'Longest Common Subsequence',
    'Lowest Common Ancestor of a Binary Search Tree',
    //'Diameter of Binary Tree',
    //'Path Sum II',
    'Binary Tree Maximum Path Sum',
    'Best Time to Buy and Sell Stock II',
    'Jump Game',
    //'Best Time to Buy and Sell Stock III',
    //'Best Time to Buy and Sell Stock with Cooldown',
    'Course Schedule II',
    'Path with Maximum Probability',
    //'Network Delay Time',
    'Path With Minimum Effort',
    'Merge Intervals',
    'Insert Interval',
    'Interval List Intersections',
    'LRU Cache',
    'Binary Search Tree Iterator',
    'Min Stack',
    'Implement Stack using Queues',
    //'Implement Queue using Stacks',
    'Letter Combinations of a Phone Number',
    'Combination Sum',
    //'Combinations',
    'Permutations',
    'Subsets',
    'Palindrome Partitioning',
    'Implement Trie (Prefix Tree)',
    //'Design Add and Search Words Data Structure',
    'Basic Calculator',
    //'Decode String',
    'Daily Temperatures',
    'Online Stock Span',
    'Partition Equal Subset Sum',
    'Redundant Connection',
    'Find Median from Data Stream',
    //'Longest Increasing Path in a Matrix',
    'Trapping Rain Water',
    'Max Points on a Line',
    'Serialize and Deserialize Binary Tree',
    'Maximum Profit in Job Scheduling',
    //'Maximum Earnings From Taxi',
    //'Min Cost to Connect All Points',
    //'Find the City With the Smallest Number of Neighbors at a Threshold Distance',
    'Sliding Window Maximum',
    'Edit Distance',
    //'Wildcard Matching',
    //'Regular Expression Matching',
    'Largest Rectangle in Histogram',
    'Number of Atoms',
    'LFU Cache',
]

// Add this helper function at the top level
const difficultyOrder = {
  'Easy': 0,
  'Medium': 1,
  'Hard': 2
};

function convertProblemToLink(problem_name) {
    return internalProblemPathFromName(problem_name);
}

function createData(number, name, lc_link, difficulty, category) {
  return { number, name, lc_link, difficulty, category };
}

function createAllData() {
  const problemData = {
    'Fizz Buzz': { difficulty: 'Easy', category: 'Math' },
    'Two Sum': { difficulty: 'Easy', category: 'Arrays & Hashing' },
    'Valid Anagram': { difficulty: 'Easy', category: 'Arrays & Hashing' },
    'Reverse String': { difficulty: 'Easy', category: 'Two Pointers' },
    'Valid Palindrome': { difficulty: 'Easy', category: 'Two Pointers' },
    'Longest Common Prefix': { difficulty: 'Easy', category: 'Arrays & Hashing' },
    'Power of Three': { difficulty: 'Easy', category: 'Math' },
    'Number of 1 Bits': { difficulty: 'Easy', category: 'Bit Manipulation' },
    'Range Sum Query - Immutable': { difficulty: 'Easy', category: 'Arrays & Hashing' },
    'Fibonacci Number': { difficulty: 'Easy', category: 'Dynamic Programming' },
    'Climbing Stairs': { difficulty: 'Easy', category: 'Dynamic Programming' },
    'Min Cost Climbing Stairs': { difficulty: 'Easy', category: 'Dynamic Programming' },
    'Best Time to Buy and Sell Stock': { difficulty: 'Easy', category: 'Dynamic Programming' },
    'Binary Tree Inorder Traversal': { difficulty: 'Easy', category: 'Trees' },
    'Invert Binary Tree': { difficulty: 'Easy', category: 'Trees' },
    'Symmetric Tree': { difficulty: 'Easy', category: 'Trees' },
    'Maximum Depth of Binary Tree': { difficulty: 'Easy', category: 'Trees' },
    'Same Tree': { difficulty: 'Easy', category: 'Trees' },
    'Path Sum': { difficulty: 'Easy', category: 'Trees' },
    'Minimum Depth of Binary Tree': { difficulty: 'Easy', category: 'Trees' },
    'Validate Binary Search Tree': { difficulty: 'Medium', category: 'Trees' },
    'Reverse Linked List': { difficulty: 'Easy', category: 'Linked List' },
    'Middle of the Linked List': { difficulty: 'Easy', category: 'Linked List' },
    'Binary Tree Level Order Traversal': { difficulty: 'Medium', category: 'Trees' },
    'Find if Path Exists in Graph': { difficulty: 'Easy', category: 'Graphs' },
    'Number of Islands': { difficulty: 'Medium', category: 'Graphs' },
    'Max Area of Island': { difficulty: 'Medium', category: 'Graphs' },
    'Count Sub Islands': { difficulty: 'Medium', category: 'Graphs' },
    'Valid Parentheses': { difficulty: 'Easy', category: 'Stack' },
    'Product of Array Except Self': { difficulty: 'Medium', category: 'Arrays & Hashing' },
    'Binary Search': { difficulty: 'Easy', category: 'Binary Search' },
    'Minimum Add to Make Parentheses Valid': { difficulty: 'Medium', category: 'Stack' },
    'Longest Palindromic Substring': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Two Sum II - Input Array Is Sorted': { difficulty: 'Medium', category: 'Two Pointers' },
    'Container With Most Water': { difficulty: 'Medium', category: 'Two Pointers' },
    '3Sum': { difficulty: 'Medium', category: 'Two Pointers' },
    'Group Anagrams': { difficulty: 'Medium', category: 'Arrays & Hashing' },
    'Kth Largest Element in an Array': { difficulty: 'Medium', category: 'Heap' },
    'K Closest Points to Origin': { difficulty: 'Medium', category: 'Heap' },
    'Insert Delete GetRandom O(1)': { difficulty: 'Medium', category: 'Design' },
    'First Bad Version': { difficulty: 'Easy', category: 'Binary Search' },
    'Find Peak Element': { difficulty: 'Medium', category: 'Binary Search' },
    'Sqrt(x)': { difficulty: 'Easy', category: 'Binary Search' },
    'Koko Eating Bananas': { difficulty: 'Medium', category: 'Binary Search' },
    'Subarray Sum Equals K': { difficulty: 'Medium', category: 'Arrays & Hashing' },
    'Continuous Subarray Sum': { difficulty: 'Medium', category: 'Arrays & Hashing' },
    'Count Number of Nice Subarrays': { difficulty: 'Medium', category: 'Sliding Window' },
    'Clone Graph': { difficulty: 'Medium', category: 'Graphs' },
    'Copy List with Random Pointer': { difficulty: 'Medium', category: 'Linked List' },
    'Is Graph Bipartite': { difficulty: 'Medium', category: 'Graphs' },
    'Evaluate Division': { difficulty: 'Medium', category: 'Graphs' },
    'Maximum Subarray': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Maximum Product Subarray': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Unique Paths': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Minimum Path Sum': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Longest Substring Without Repeating Characters': { difficulty: 'Medium', category: 'Sliding Window' },
    'Max Consecutive Ones III': { difficulty: 'Medium', category: 'Sliding Window' },
    'Maximize the Confusion of An Exam': { difficulty: 'Medium', category: 'Sliding Window' },
    'Longest Nice Subarray': { difficulty: 'Medium', category: 'Sliding Window' },
    'Random Pick with Weight': { difficulty: 'Medium', category: 'Binary Search' },
    'Word Break': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'House Robber': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Coin Change': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Longest Increasing Subsequence': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Longest Common Subsequence': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Lowest Common Ancestor of a Binary Search Tree': { difficulty: 'Medium', category: 'Trees' },
    'Binary Tree Maximum Path Sum': { difficulty: 'Hard', category: 'Trees' },
    'Best Time to Buy and Sell Stock II': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Jump Game': { difficulty: 'Medium', category: 'Greedy' },
    'Course Schedule II': { difficulty: 'Medium', category: 'Graphs' },
    'Path with Maximum Probability': { difficulty: 'Medium', category: 'Graphs' },
    'Path With Minimum Effort': { difficulty: 'Medium', category: 'Graphs' },
    'Merge Intervals': { difficulty: 'Medium', category: 'Intervals' },
    'Insert Interval': { difficulty: 'Medium', category: 'Intervals' },
    'Interval List Intersections': { difficulty: 'Medium', category: 'Intervals' },
    'LRU Cache': { difficulty: 'Medium', category: 'Design' },
    'Binary Search Tree Iterator': { difficulty: 'Medium', category: 'Trees' },
    'Min Stack': { difficulty: 'Medium', category: 'Stack' },
    'Implement Stack using Queues': { difficulty: 'Easy', category: 'Stack' },
    'Letter Combinations of a Phone Number': { difficulty: 'Medium', category: 'Backtracking' },
    'Combination Sum': { difficulty: 'Medium', category: 'Backtracking' },
    'Permutations': { difficulty: 'Medium', category: 'Backtracking' },
    'Subsets': { difficulty: 'Medium', category: 'Backtracking' },
    'Palindrome Partitioning': { difficulty: 'Medium', category: 'Backtracking' },
    'Implement Trie (Prefix Tree)': { difficulty: 'Medium', category: 'Trie' },
    'Basic Calculator': { difficulty: 'Hard', category: 'Stack' },
    'Daily Temperatures': { difficulty: 'Medium', category: 'Stack' },
    'Online Stock Span': { difficulty: 'Medium', category: 'Stack' },
    'Partition Equal Subset Sum': { difficulty: 'Medium', category: 'Dynamic Programming' },
    'Redundant Connection': { difficulty: 'Medium', category: 'Graphs' },
    'Find Median from Data Stream': { difficulty: 'Hard', category: 'Heap' },
    'Trapping Rain Water': { difficulty: 'Hard', category: 'Two Pointers' },
    'Max Points on a Line': { difficulty: 'Hard', category: 'Math' },
    'Serialize and Deserialize Binary Tree': { difficulty: 'Hard', category: 'Trees' },
    'Maximum Profit in Job Scheduling': { difficulty: 'Hard', category: 'Dynamic Programming' },
    'Sliding Window Maximum': { difficulty: 'Hard', category: 'Sliding Window' },
    'Edit Distance': { difficulty: 'Hard', category: 'Dynamic Programming' },
    'Largest Rectangle in Histogram': { difficulty: 'Hard', category: 'Stack' },
    'Number of Atoms': { difficulty: 'Hard', category: 'Stack' },
    'LFU Cache': { difficulty: 'Hard', category: 'Design' }
  };

    let res = []
    for (let i = 0; i < lc_questions.length; i++) {
    const name = lc_questions[i];
    const data = problemData[name] || { difficulty: 'Medium', category: 'Unknown' };
    res.push(createData(
      i+1, 
      name, 
      convertProblemToLink(name),
      data.difficulty,
      data.category
    ));
  }
  return res;
}

const rows = createAllData()

// Create styled components for responsive table
const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100vw',
    overflowX: 'auto'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  whiteSpace: 'normal',  // Prevent text wrapping
  padding: theme.spacing(1),  // Reduce padding on mobile
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    fontSize: '0.8rem'  // Smaller font on mobile
  }
}));

export default function BasicTable() {
  const { isDarkMode } = useTheme();

  // Get solved problems from localStorage or empty object if none exist
  const [solvedProblems, setSolvedProblems] = useState(() => {
    const saved = localStorage.getItem('solvedProblems');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Add sorting state
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Add filter state to use arrays
  const [filterConfig, setFilterConfig] = useState({
    categories: [],
    difficulties: []
  });

  // Sort function
  const sortedAndFilteredRows = React.useMemo(() => {
    // First apply filters
    let filteredItems = [...rows];
    if (filterConfig.categories.length > 0) {
      filteredItems = filteredItems.filter(row => filterConfig.categories.includes(row.category));
    }
    if (filterConfig.difficulties.length > 0) {
      filteredItems = filteredItems.filter(row => filterConfig.difficulties.includes(row.difficulty));
    }

    // Then sort
    if (sortConfig.key !== null) {
      filteredItems.sort((a, b) => {
        if (sortConfig.key === 'done') {
          const aCompleted = !!solvedProblems[a.number];
          const bCompleted = !!solvedProblems[b.number];
          return sortConfig.direction === 'asc' 
            ? (aCompleted === bCompleted ? 0 : aCompleted ? -1 : 1)
            : (aCompleted === bCompleted ? 0 : aCompleted ? 1 : -1);
        }

        // Special handling for difficulty
        if (sortConfig.key === 'difficulty') {
          const aOrder = difficultyOrder[a.difficulty];
          const bOrder = difficultyOrder[b.difficulty];
          return sortConfig.direction === 'asc' 
            ? aOrder - bOrder
            : bOrder - aOrder;
        }
        
        // Default sorting for other columns
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return filteredItems;
  }, [sortConfig, solvedProblems, filterConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return null;
    }
    return sortConfig.direction === 'asc' ? <ArrowUpward /> : <ArrowDownward />;
  };

  // Save to localStorage whenever solvedProblems changes
  useEffect(() => {
    localStorage.setItem('solvedProblems', JSON.stringify(solvedProblems));
  }, [solvedProblems]);

  const handleToggle = (problemId) => {
    setSolvedProblems(prev => ({
      ...prev,
      [problemId]: !prev[problemId]
    }));
  };

  // Calculate progress statistics
  const progressStats = React.useMemo(() => {
    const total = rows.length;
    const completed = Object.values(solvedProblems).filter(Boolean).length;
    
    // By difficulty
    const difficultyStats = rows.reduce((acc, row) => {
      if (!acc[row.difficulty]) {
        acc[row.difficulty] = {
          total: 0,
          completed: 0
        };
      }
      acc[row.difficulty].total++;
      if (solvedProblems[row.number]) {
        acc[row.difficulty].completed++;
      }
      return acc;
    }, {});

    // By category
    const categoryStats = rows.reduce((acc, row) => {
      if (!acc[row.category]) {
        acc[row.category] = {
          total: 0,
          completed: 0
        };
      }
      acc[row.category].total++;
      if (solvedProblems[row.number]) {
        acc[row.category].completed++;
      }
      return acc;
    }, {});

    return {
      total,
      completed,
      difficultyStats,
      categoryStats
    };
  }, [solvedProblems]);

  const recentRuns = React.useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('problemRunStats') || '{}');
      return Object.values(saved)
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 4);
    } catch {
      return [];
    }
  }, []);

  const recommendations = React.useMemo(() => {
    const weakestCategories = Object.entries(progressStats.categoryStats)
      .map(([category, stats]) => ({
        category,
        completion: stats.total ? stats.completed / stats.total : 0,
      }))
      .sort((a, b) => a.completion - b.completion)
      .map((item) => item.category);

    const picks = [];
    weakestCategories.forEach((category) => {
      const next = rows.find((row) => row.category === category && !solvedProblems[row.number] && !picks.some((pick) => pick.name === row.name));
      if (next) picks.push(next);
    });

    return picks.slice(0, 3);
  }, [progressStats.categoryStats, solvedProblems]);

  // Add this state
  const [showCategories, setShowCategories] = useState(false);

  // Update the filter buttons click handlers
  const handleCategoryFilter = (category) => {
    setFilterConfig(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleDifficultyFilter = (difficulty) => {
    setFilterConfig(prev => ({
      ...prev,
      difficulties: prev.difficulties.includes(difficulty)
        ? prev.difficulties.filter(d => d !== difficulty)
        : [...prev.difficulties, difficulty]
    }));
  };

  // Update clear filters
  const clearFilters = () => {
    setFilterConfig({
      categories: [],
      difficulties: []
    });
  };

  const difficultyTone = {
    Easy: { color: '#15905f', bg: 'rgba(21, 144, 95, 0.1)' },
    Medium: { color: '#b7791f', bg: 'rgba(183, 121, 31, 0.12)' },
    Hard: { color: '#c24150', bg: 'rgba(194, 65, 80, 0.1)' },
  };

  const StatPanel = ({ label, value, detail }) => (
    <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 850, mt: 0.5 }}>
        {value}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
        {detail}
      </Typography>
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
      <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 5 }}>
        <Typography
          variant="overline"
          sx={{
            display: 'inline-flex',
            px: 2,
            py: 0.6,
            mb: 2,
            borderRadius: 999,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 800,
          }}
        >
          TopSWE100
        </Typography>
        <Typography variant="h2" sx={{ color: 'text.primary', fontSize: { xs: '2.7rem', md: '4.8rem' }, lineHeight: 0.95, fontWeight: 850 }}>
          The interview problem set.
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.7, mt: 2 }}>
          Track the essential problems by pattern, difficulty, and completion status.
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
                Total progress
              </Typography>
              <Typography variant="h3" sx={{ color: 'text.primary', fontWeight: 850 }}>
                {progressStats.completed} / {progressStats.total}
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ color: 'secondary.main', fontWeight: 850 }}>
              {Math.round((progressStats.completed / progressStats.total) * 100)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(progressStats.completed / progressStats.total) * 100}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: 'rgba(81, 104, 255, 0.12)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                background: 'linear-gradient(90deg, #5168ff, #73d5ff)',
              },
            }}
          />
        </Paper>
        {['Easy', 'Medium', 'Hard'].map((difficulty) => {
          const stats = progressStats.difficultyStats[difficulty];
          const tone = difficultyTone[difficulty];
          return (
            <StatPanel
              key={difficulty}
              label={difficulty}
              value={`${stats.completed}/${stats.total}`}
              detail={`${Math.round((stats.completed / stats.total) * 100)}% complete`}
              sx={{ color: tone.color }}
            />
          );
        })}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.15fr 0.85fr' }, gap: 2, mb: 3 }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 850 }}>
            Recommended next
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 850, mb: 2 }}>
            Practice where your coverage is thinnest.
          </Typography>
          <Stack spacing={1.2}>
            {recommendations.map((problem) => (
              <Box
                key={problem.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: 'action.hover',
                }}
              >
                <Box>
                  <Typography sx={{ color: 'text.primary', fontWeight: 800 }}>{problem.name}</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '0.86rem' }}>{problem.category}</Typography>
                </Box>
                <Button component={RouterLink} to={problem.lc_link} size="small" variant="contained">
                  Solve
                </Button>
              </Box>
            ))}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 850 }}>
            Recent judge runs
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 850, mb: 2 }}>
            Execution history
          </Typography>
          {recentRuns.length ? (
            <Stack spacing={1}>
              {recentRuns.map((run) => (
                <Box key={`${run.title}-${run.updatedAt}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ color: 'text.primary', fontWeight: 750, fontSize: '0.92rem' }}>{run.title}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                      {run.passedCount}/{run.totalCount} tests · {run.language}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={run.passed ? 'Passed' : 'Review'}
                    sx={{ color: run.passed ? 'success.main' : 'warning.main', bgcolor: 'action.hover' }}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              Run tests in the internal editor and this panel will become a lightweight practice log.
            </Typography>
          )}
        </Paper>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
              Filters
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem' }}>
              {filterConfig.categories.length + filterConfig.difficulties.length} active filters
            </Typography>
          </Box>
          {(filterConfig.categories.length > 0 || filterConfig.difficulties.length > 0) && (
            <Button variant="text" size="small" onClick={clearFilters}>
              Clear
            </Button>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
            Category
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {Array.from(new Set(rows.map(row => row.category))).sort().map(category => (
              <Button
                key={category}
                variant={filterConfig.categories.includes(category) ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleCategoryFilter(category)}
              >
                {category}
              </Button>
            ))}
          </Box>
        </Box>

        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
          Difficulty
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {['Easy', 'Medium', 'Hard'].map(difficulty => {
            const tone = difficultyTone[difficulty];
            const selected = filterConfig.difficulties.includes(difficulty);
            return (
              <Button
                key={difficulty}
                variant={selected ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleDifficultyFilter(difficulty)}
                sx={{
                  color: selected ? 'primary.contrastText' : tone.color,
                  borderColor: tone.color,
                  bgcolor: selected ? tone.color : 'transparent',
                  '&:hover': {
                    bgcolor: selected ? tone.color : tone.bg,
                    borderColor: tone.color,
                  },
                }}
              >
                {difficulty}
              </Button>
            );
          })}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
          Category progress
        </Typography>
        <IconButton onClick={() => setShowCategories(!showCategories)} sx={{ color: 'text.primary' }}>
          {showCategories ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {showCategories && (
        <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {Object.entries(progressStats.categoryStats)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([category, stats]) => (
                <Box key={category}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                      {category}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                      {stats.completed} / {stats.total}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(stats.completed / stats.total) * 100}
                    sx={{ 
                      height: 4, 
                      borderRadius: 999,
                      backgroundColor: 'rgba(81, 104, 255, 0.12)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #5168ff, #73d5ff)',
                        borderRadius: 999,
                      }
                    }} 
                  />
                </Box>
            ))}
          </Box>
        </Paper>
      )}
      
      <ResponsiveTableContainer component={Paper}>
      <Table sx={{ minWidth: 760 }} aria-label="TopSWE100 problem table">
        <TableHead>
            <TableRow>
              <StyledTableCell 
                sx={{ width: { xs: '52px', sm: '70px' }, cursor: 'pointer' }}
                onClick={() => requestSort('done')}
              >
                Done {getSortIcon('done')}
              </StyledTableCell>
              <StyledTableCell 
                sx={{ width: { xs: '56px', sm: '70px' }, cursor: 'pointer' }}
                onClick={() => requestSort('number')}
              >
                # {getSortIcon('number')}
              </StyledTableCell>
              <StyledTableCell 
                sx={{ cursor: 'pointer' }}
                onClick={() => requestSort('name')}
              >
                Problem {getSortIcon('name')}
              </StyledTableCell>
              <StyledTableCell 
                sx={{ width: { xs: '120px', sm: '150px' }, cursor: 'pointer' }}
                onClick={() => requestSort('difficulty')}
              >
                Difficulty {getSortIcon('difficulty')}
              </StyledTableCell>
              <StyledTableCell 
                sx={{ width: { xs: '160px', sm: '210px' }, cursor: 'pointer' }}
                onClick={() => requestSort('category')}
              >
                Category {getSortIcon('category')}
              </StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
            {sortedAndFilteredRows.map((row) => (
            <TableRow
              key={row.name}
                sx={{
                  bgcolor: solvedProblems[row.number] ? 'rgba(21, 144, 95, 0.06)' : 'transparent',
                  '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(81, 104, 255, 0.045)' },
                }}
              >
                <StyledTableCell>
                  <Checkbox
                    checked={!!solvedProblems[row.number]}
                    onChange={() => handleToggle(row.number)}
                    sx={{
                      color: 'secondary.main',
                      '&.Mui-checked': {
                        color: '#15905f',
                      },
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>{row.number}</StyledTableCell>
                <StyledTableCell>
                  <Button
                    component={RouterLink}
                    to={row.lc_link}
                    variant="text"
                    style={{ 
                      color: 'inherit', 
                      textDecoration: 'none',
                      fontWeight: 750,
                      padding: 0,
                      minWidth: 0,
                      textTransform: 'none',
                    }}
                  >
                {row.name}
                  </Button>
                </StyledTableCell>
                <StyledTableCell>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      px: 1.2,
                      py: 0.45,
                      borderRadius: 999,
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      color: difficultyTone[row.difficulty].color,
                      bgcolor: difficultyTone[row.difficulty].bg,
                    }}
                  >
                    {row.difficulty}
                  </Box>
                </StyledTableCell>
                <StyledTableCell sx={{ color: 'text.secondary', fontWeight: 650 }}>{row.category}</StyledTableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </ResponsiveTableContainer>
    </Container>
  );
}
